import { App } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';

let navigationCallback: ((path: string) => void) | null = null;
let isInitialized = false;

/**
 * Set the navigation callback for deep link routing
 */
export const setDeepLinkNavigator = (navigate: (path: string) => void) => {
  navigationCallback = navigate;
};

/**
 * Handle OAuth auth callback deep links
 * URL format: decorcanvas://auth#access_token=xxx&refresh_token=yyy
 */
const handleAuthDeepLink = async (url: string): Promise<boolean> => {
  try {
    const urlObj = new URL(url);
    
    // Check if this is an auth callback
    if (urlObj.protocol !== 'decorcanvas:' || urlObj.host !== 'auth') {
      return false;
    }

    console.log('[DeepLink] Auth callback detected');
    
    // Extract tokens from the URL fragment (after #)
    const fragment = urlObj.hash.substring(1); // Remove the leading #
    if (!fragment) {
      console.error('[DeepLink] No fragment in auth URL');
      return false;
    }

    const params = new URLSearchParams(fragment);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (!accessToken || !refreshToken) {
      console.error('[DeepLink] Missing tokens in auth callback');
      return false;
    }

    console.log('[DeepLink] Setting session with tokens');
    
    // Set the session using the tokens
    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      console.error('[DeepLink] Failed to set session:', error.message);
      return false;
    }

    console.log('[DeepLink] Session set successfully');
    
    // Navigate to designer page
    if (navigationCallback) {
      navigationCallback('/designer');
    }

    return true;
  } catch (error) {
    console.error('[DeepLink] Error handling auth deep link:', error);
    return false;
  }
};

/**
 * Handle payment callback deep links
 * URL format: decorcanvas://payment?status=success
 */
const handlePaymentDeepLink = async (url: string): Promise<boolean> => {
  try {
    const urlObj = new URL(url);
    
    if (urlObj.protocol !== 'decorcanvas:' || urlObj.host !== 'payment') {
      return false;
    }

    console.log('[DeepLink] Payment callback detected');
    
    // Navigate to payment return page with query params
    if (navigationCallback) {
      navigationCallback(`/payment-return${urlObj.search}`);
    }

    return true;
  } catch (error) {
    console.error('[DeepLink] Error handling payment deep link:', error);
    return false;
  }
};

/**
 * Initialize deep link handler
 * Should be called once when the app starts on native platforms
 */
export const initDeepLinkHandler = async () => {
  if (isInitialized) {
    console.log('[DeepLink] Handler already initialized');
    return;
  }

  console.log('[DeepLink] Initializing deep link handler');
  
  // Listen for app URL open events
  App.addListener('appUrlOpen', async (event) => {
    console.log('[DeepLink] App opened with URL:', event.url);
    
    // Try auth handler first
    const handledByAuth = await handleAuthDeepLink(event.url);
    if (handledByAuth) return;
    
    // Try payment handler
    const handledByPayment = await handlePaymentDeepLink(event.url);
    if (handledByPayment) return;
    
    console.log('[DeepLink] URL not handled:', event.url);
  });

  isInitialized = true;
  console.log('[DeepLink] Handler initialized');
};
